import { useMemo } from "react";
import { useSearchParams } from "react-router";
import i18n from "~/i18n";

type GetValue<T> = (item: T) => string | string[];

export interface Filter<T> {
  id: string;
  label: string;
  options: {
    value: string;
    label: string;
  }[];
  getValue: GetValue<T>;
}

export interface FilterWithOption<T> extends Filter<T> {
  optionId: string;
}

const allOption = {
  value: "all",
  label: i18n.t("commons.all"),
};

export function useFilter<T>(filterInput: Filter<T>[]) {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsString = searchParams.toString();

  const filters = useMemo(() => {
    // Rebuild URLSearchParams from the stable string so the dependency array only contains simple expressions
    const params = new URLSearchParams(searchParamsString);
    return filterInput.map((filter) => ({
      ...filter,
      options: [allOption, ...filter.options],
      optionId: params.get(filter.id) ?? allOption.value,
    }));
  }, [filterInput, searchParamsString]);

  const handleSelectFilter = (filterId: string, optionId: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (optionId === "all") {
        newParams.delete(filterId);
      } else {
        newParams.set(filterId, optionId);
      }
      return newParams;
    });
  };

  const handleClearFilters = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      filters.forEach((filter) => {
        newParams.delete(filter.id);
      });
      return newParams;
    });
  };

  const activeFilters = filters.filter((filter) => filter.optionId !== "all");

  const filterItems = (items: T[]) =>
    items.filter((item) =>
      activeFilters.every(({ optionId, getValue }) => {
        const itemValue = getValue(item);
        return Array.isArray(itemValue)
          ? itemValue.some((value) => value === optionId)
          : itemValue === optionId;
      }),
    );

  return {
    filters,
    activeFilters,
    handleClearFilters,
    handleSelectFilter,
    filterItems,
  };
}
